import { Link, useLoaderData } from 'react-router';
import { api } from '@/lib/api';
import { fmtAgo } from '@/lib/format';
import { RowLink } from '@/components/row-link';

type Template = { id: string; name: string; subject: string; kind: string; currentVersion: number; mergeTags: string[]; testSentAt: string | null; isExample: boolean; styleId: string | null; updatedAt: string; createdBy: string };
type Style = { id: string; name: string };

export async function templatesLoader() {
  const [templates, styles] = await Promise.all([api<Template[]>('/templates'), api<Style[]>('/styles')]);
  return { templates, styles };
}

export function TemplatesPage() {
  const { templates, styles } = useLoaderData<typeof templatesLoader>();
  const styleName = (id: string | null) => styles.find((s) => s.id === id)?.name ?? null;
  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl">Templates</h1>
          <p className="text-sm text-muted-foreground">HTML emails stored in Firestore, versioned. Generate with AI, import HTML / a zip, edit visually or with a prompt.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/templates/styles" className="ff-link text-sm">
            Style library ({styles.length})
          </Link>
          <Link to="/templates/new" className="rounded-md bg-[var(--ff-primary)] px-4 py-2 text-sm text-white">
            New template
          </Link>
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border bg-white">
        <table className="data-table">
          <thead>
            <tr>
              <th>Template</th>
              <th>Style</th>
              <th>Kind</th>
              <th>Version</th>
              <th>Merge tags</th>
              <th>Test sent</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {templates.length === 0 && (
              <tr>
                <td colSpan={7} className="py-8 text-center text-muted-foreground">
                  No templates yet.
                </td>
              </tr>
            )}
            {templates.map((t) => (
              <RowLink key={t.id} href={`/templates/${t.id}`}>
                <td>
                  <Link to={`/templates/${t.id}`} className="ff-link font-medium">
                    {t.name}
                  </Link>
                  {t.isExample && <span className="ml-2 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] uppercase text-violet-800">AI example</span>}
                  <div className="text-xs text-muted-foreground">{t.subject}</div>
                </td>
                <td className="text-xs">{t.styleId ? <Link to={`/templates/styles/${t.styleId}`} className="ff-link">{styleName(t.styleId)}</Link> : <span className="text-muted-foreground">—</span>}</td>
                <td>{t.kind}</td>
                <td>v{t.currentVersion}</td>
                <td className="text-xs text-muted-foreground">{t.mergeTags.join(', ')}</td>
                <td className="whitespace-nowrap text-xs">{t.testSentAt ? fmtAgo(t.testSentAt) : <span className="text-amber-700">never</span>}</td>
                <td className="whitespace-nowrap text-muted-foreground">{fmtAgo(t.updatedAt)}</td>
              </RowLink>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
